import { useEffect } from 'react'

interface Props {
	open: boolean
	title: string
	description: React.ReactNode
	confirmLabel: string
	tone?: 'primary' | 'danger'
	busy?: boolean
	onConfirm: () => void
	onCancel: () => void
}

// Every irreversible or outward-facing action goes through this — cancelling a
// subscription and impersonating a customer both reach real people.
export function ConfirmDialog({ open, title, description, confirmLabel, tone = 'primary', busy, onConfirm, onCancel }: Props) {
	useEffect(() => {
		if (!open) return
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCancel()
		document.addEventListener('keydown', onKey)
		document.body.style.overflow = 'hidden'
		return () => {
			document.removeEventListener('keydown', onKey)
			document.body.style.overflow = ''
		}
	}, [open, onCancel])

	if (!open) return null

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
			<button type="button" aria-hidden className="absolute inset-0 bg-black/40" onClick={onCancel} tabIndex={-1} />
			<div role="dialog" aria-modal className="card relative z-10 w-full max-w-md p-5">
				<h2 className="font-display text-lg font-semibold">{title}</h2>
				<div className="mt-2 text-sm text-ink-soft">{description}</div>
				<div className="mt-5 flex justify-end gap-2">
					<button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
						Cancel
					</button>
					<button
						type="button"
						className={tone === 'danger' ? 'btn-danger' : 'btn-primary'}
						onClick={onConfirm}
						disabled={busy}
					>
						{busy ? 'Working…' : confirmLabel}
					</button>
				</div>
			</div>
		</div>
	)
}
